import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { getOnboardingStatus, postOnboardingDismiss } from '../../lib/api';

/**
 * Renders a setup checklist for owners who are configuring Hisland Time for
 * the first time. Auto-hides when all 5 items are complete or when dismissed.
 * Visible to owners only — the parent (AdminDashboard) guards rendering with
 * isOwner, but we add a belt-and-braces check here as well.
 *
 * TODO: data is fetched from GET /api/owner/onboarding/status on mount.
 * The parent dashboard re-mounts on navigation, so the status refreshes
 * automatically each time the owner returns from completing a setup step.
 */

const CHECKLIST_ITEMS = [
    {
        slug: 'profile',
        statusKey: 'profile_complete',
        to: '/dashboard/company',
    },
    {
        slug: 'logo',
        statusKey: 'logo_uploaded',
        to: '/dashboard/company?tab=branding',
    },
    {
        slug: 'admin_invite',
        statusKey: 'first_admin_invited',
        to: '/dashboard/employees',
    },
    {
        slug: 'payroll',
        statusKey: 'payroll_configured',
        to: '/dashboard/settings?tab=time-policy',
    },
    {
        slug: 'fortnox',
        statusKey: 'fortnox_connected',
        to: '/dashboard/integrations',
    },
];

export default function OnboardingChecklist() {
    const { t } = useTranslation();
    const { isOwner } = useAuth();
    const navigate = useNavigate();

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissing, setDismissing] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await getOnboardingStatus();
            setStatus(res.data);
        } catch {
            // Silently fail — if the endpoint errors the checklist stays hidden
            setStatus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isOwner) return;
        fetchStatus();
    }, [isOwner, fetchStatus]);

    const handleDismiss = async () => {
        setDismissing(true);
        try {
            await postOnboardingDismiss();
            // Optimistic hide — update local state immediately
            setStatus((prev) => ({ ...prev, dismissed_at: new Date().toISOString() }));
        } catch {
            toast.error(t('onboarding.dismiss_error_toast'));
        } finally {
            setDismissing(false);
        }
    };

    // Belt-and-braces owner guard
    if (!isOwner) return null;
    // Still loading first response
    if (loading) return null;
    // Endpoint failed
    if (!status) return null;
    // Already dismissed
    if (status.dismissed_at) return null;
    // All complete — auto-hide
    if (status.all_complete) return null;

    const completedCount = CHECKLIST_ITEMS.filter((item) => status[item.statusKey]).length;

    return (
        <Card className="border border-border shadow-none" data-testid="onboarding-checklist">
            <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
                    {t('onboarding.welcome_title')}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                    {t('onboarding.welcome_intro')}
                </p>
                <p className="text-xs text-muted-foreground">
                    {completedCount}/{CHECKLIST_ITEMS.length}
                </p>
            </CardHeader>

            <CardContent className="pt-0">
                <ul className="space-y-3">
                    {CHECKLIST_ITEMS.map((item) => {
                        const isDone = !!status[item.statusKey];
                        return (
                            <li
                                key={item.slug}
                                className="flex items-center gap-3"
                                data-testid={`onboarding-item-${item.slug}`}
                            >
                                {isDone ? (
                                    <CheckCircle2
                                        className="w-5 h-5 text-green-600 flex-shrink-0"
                                        strokeWidth={1.5}
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <Circle
                                        className="w-5 h-5 text-muted-foreground flex-shrink-0"
                                        strokeWidth={1.5}
                                        aria-hidden="true"
                                    />
                                )}

                                <div className="flex-1 min-w-0">
                                    <p
                                        className={`text-sm font-medium leading-none ${
                                            isDone ? 'text-muted-foreground line-through' : ''
                                        }`}
                                    >
                                        {t(`onboarding.item.${item.slug}.title`)}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {t(`onboarding.item.${item.slug}.subtitle`)}
                                    </p>
                                </div>

                                {!isDone && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-shrink-0 uppercase tracking-wider"
                                        onClick={() => navigate(item.to)}
                                        aria-label={t(`onboarding.item.${item.slug}.title`)}
                                    >
                                        {t('onboarding.go_button')}
                                    </Button>
                                )}
                            </li>
                        );
                    })}
                </ul>

                <div className="mt-4 pt-3 border-t border-border">
                    <Button
                        variant="link"
                        size="sm"
                        className="text-muted-foreground px-0 h-auto text-xs"
                        onClick={handleDismiss}
                        disabled={dismissing}
                        data-testid="onboarding-dismiss"
                    >
                        {t('onboarding.dismiss_link')}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
